import type { WorkbookInstance } from '@fortune-sheet/react';
import type { WorkbookInternals } from './workbookInternals';
import type { LaneAActionId } from './shortcutKeymap';
import {
    fillSelection,
    selectRowsOrCols,
    switchSheet,
    navHome,
    navCtrlHome,
    navCtrlEnd,
    toggleFilter,
    autoSum,
    enterMove,
    commitAndMove,
    toggleFontStyle,
    extendHome,
    extendCtrlHome,
    extendCtrlEnd,
    deleteCells,
    insertCells,
    hideOrShow,
    insertDate,
    insertTime,
    copyAbove,
    insertLink,
    numFmtPreset,
    openCellDropdown,
} from './sheetActions';
import { handlePointMode, handleCycleRef, FORMULA_SEARCH_ID } from './formulaEditActions';
import type { ArrowKey } from './formulaPointMode';

/** Everything a Lane A action might need — a strict superset, since each
 * case only reads the fields its own action requires. */
export interface LaneADispatchDeps {
    internals: WorkbookInternals | null;
    wb: WorkbookInstance | null;
    target: EventTarget | null;
    key: string;
    /** Needed only by `numFmtPreset`, which matches on the physical key
     *  (layout-independent) rather than the shifted character. */
    code: string;
    shiftKey: boolean;
    onOpenGoTo: () => void;
    onOpenFormatCells: () => void;
    onArmValuesPaste: () => void;
    onOpenPasteSpecial: () => void;
    onOpenDeleteInsert: (mode: 'delete' | 'insert') => void;
}

/** Tells the caller whether to `preventDefault`/`stopPropagation` after
 * dispatch: `'handled'` always does; `'handled-no-prevent'` (pasteValues)
 * never does, so the browser still delivers the following native paste
 * event; `'not-handled'` means the action declined to act (missing
 * workbook/internals, or a point-mode/cycle-ref key that isn't at an
 * insertable position) and the key should be left alone. */
export type LaneADispatchResult = 'not-handled' | 'handled' | 'handled-no-prevent';

/**
 * Pure Lane A dispatcher: one exhaustive switch over every
 * `LaneAActionId`, each case a named action from `sheetActions.ts` /
 * `formulaEditActions.ts`. Never reads component state directly — every
 * input comes through `deps`.
 */
export const dispatchLaneA = (actionId: LaneAActionId, deps: LaneADispatchDeps): LaneADispatchResult => {
    const {
        internals,
        wb,
        target,
        key,
        code,
        shiftKey,
        onOpenGoTo,
        onOpenFormatCells,
        onArmValuesPaste,
        onOpenPasteSpecial,
        onOpenDeleteInsert,
    } = deps;
    switch (actionId) {
        case 'swallowSheetFocusToggle':
            // Swallow only — dropping the sheetFocused re-arm here is fine
            // since Lane A already keeps every shortcut in this table working
            // regardless of the library's own focus flag.
            return 'handled';
        case 'pasteValues':
            // Never prevents default, even once armed — the browser
            // must still deliver the following native paste event.
            onArmValuesPaste();
            return 'handled-no-prevent';
        case 'openPasteSpecial':
            onOpenPasteSpecial();
            return 'handled';
        case 'pointMode':
            // Decides interception itself (a caret that isn't at an
            // insertable position isn't handled — the key must move the
            // caret natively instead).
            if (!internals || !handlePointMode(internals, target, key as ArrowKey, shiftKey)) return 'not-handled';
            return 'handled';
        case 'cycleRef':
            // Decides interception itself (no ref under the caret isn't
            // handled).
            if (!internals || !handleCycleRef(internals, target)) return 'not-handled';
            return 'handled';
        case 'fillSelection':
            if (!internals) return 'not-handled';
            fillSelection(internals);
            return 'handled';
        case 'selectColumns':
            if (!wb) return 'not-handled';
            selectRowsOrCols(wb, 'col');
            return 'handled';
        case 'selectRows':
            if (!wb) return 'not-handled';
            selectRowsOrCols(wb, 'row');
            return 'handled';
        case 'switchSheet':
            if (!wb) return 'not-handled';
            switchSheet(wb, key === 'PageUp' || key === 'ArrowLeft' ? 'prev' : 'next');
            return 'handled';
        case 'navHome':
            if (!wb) return 'not-handled';
            navHome(wb, internals);
            return 'handled';
        case 'navCtrlHome':
            if (!wb) return 'not-handled';
            navCtrlHome(wb);
            return 'handled';
        case 'navCtrlEnd':
            if (!wb) return 'not-handled';
            navCtrlEnd(wb);
            return 'handled';
        case 'openGoTo':
            onOpenGoTo();
            return 'handled';
        case 'openFormatCells':
            onOpenFormatCells();
            return 'handled';
        case 'toggleFilter':
            if (!internals) return 'not-handled';
            toggleFilter(internals);
            return 'handled';
        case 'autoSum':
            if (!internals) return 'not-handled';
            autoSum(internals);
            return 'handled';
        case 'enterMove':
            if (!internals) return 'not-handled';
            enterMove(internals, key === 'Enter' && shiftKey ? 'up' : 'down');
            return 'handled';
        case 'tabCommit':
            if (!internals) return 'not-handled';
            // The autocomplete dropdown accepts its own suggestion on Tab —
            // decline so the library's own handler runs instead.
            if (key === 'Tab' && document.getElementById(FORMULA_SEARCH_ID)) return 'not-handled';
            commitAndMove(internals, key === 'Enter' ? 'up' : shiftKey ? 'left' : 'right');
            return 'handled';
        case 'italic':
        case 'underline':
            if (!internals) return 'not-handled';
            toggleFontStyle(internals, actionId);
            return 'handled';
        case 'extendHome':
            if (!wb) return 'not-handled';
            extendHome(wb, internals);
            return 'handled';
        case 'extendCtrlHome':
            if (!wb) return 'not-handled';
            extendCtrlHome(wb, internals);
            return 'handled';
        case 'extendCtrlEnd':
            if (!wb) return 'not-handled';
            extendCtrlEnd(wb, internals);
            return 'handled';
        case 'deleteCells':
            if (!wb || !internals) return 'not-handled';
            deleteCells(wb, internals, () => onOpenDeleteInsert('delete'));
            return 'handled';
        case 'insertCells':
            if (!wb || !internals) return 'not-handled';
            insertCells(wb, internals, () => onOpenDeleteInsert('insert'));
            return 'handled';
        case 'hideRows':
            if (!internals) return 'not-handled';
            hideOrShow(internals, 'row', false);
            return 'handled';
        case 'hideCols':
            if (!internals) return 'not-handled';
            hideOrShow(internals, 'column', false);
            return 'handled';
        case 'unhideRows':
            if (!internals) return 'not-handled';
            hideOrShow(internals, 'row', true);
            return 'handled';
        case 'unhideCols':
            if (!internals) return 'not-handled';
            hideOrShow(internals, 'column', true);
            return 'handled';
        case 'insertDate':
            if (!wb || !internals) return 'not-handled';
            insertDate(wb, internals);
            return 'handled';
        case 'insertTime':
            if (!wb || !internals) return 'not-handled';
            insertTime(wb, internals);
            return 'handled';
        case 'copyAbove':
            if (!wb || !internals) return 'not-handled';
            copyAbove(wb, internals);
            return 'handled';
        case 'insertLink':
            if (!internals) return 'not-handled';
            insertLink(internals);
            return 'handled';
        case 'numFmtPreset':
            if (!wb || !internals) return 'not-handled';
            numFmtPreset(wb, internals, code);
            return 'handled';
        case 'openCellDropdown':
            // Decides interception itself — a plain cell (neither dropdown
            // present) is left unhandled, exactly like pointMode/cycleRef.
            if (!wb || !openCellDropdown(wb, internals)) return 'not-handled';
            return 'handled';
        default: {
            const exhaustiveCheck: never = actionId;
            return exhaustiveCheck;
        }
    }
};
